import { ChatMessage, DisplayChatMessage, UserChat } from 'interfaces/UserChat'
import ThreadIcon from 'assets/icons/thread-messages.svg'
import { Body2 } from 'stories/elements/Typography/Text'
import { useCallback, useState } from 'react'
import { useToggleState } from 'lib/hooks/useToggleState'
import UserChatService from 'services/UserChatService'
import _useTranslation from 'lib/hooks/_useTranslation'
import { ApiError } from 'services/ApiService'
import { User } from 'interfaces/User'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import ChatDisplay from '../display/ChatDisplay'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import styled from 'styled-components'
import { generateDisplayMessages } from 'lib/features/chat'
import theme from 'stories/utils/theme'
import ChatMessageForm from '../display/ChatMessageForm'
import { ContentPrefixes } from 'lib/constants/ContentPrefixes'
import { AnalyticsEventChatSend } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const ThreadWrapper = styled.div`
    display: flex;
    gap: 2px;
    cursor: pointer;
    align-items: center;
    padding-top: 5px;
    p {
        position: relative;
        top: 1px;
    }
`

interface Props {
    chat: UserChat
    chatMessage: DisplayChatMessage
    groupMembers: User[]
    fetchData?: () => void
}

const ThreadMessages = ({ fetchData, chat, chatMessage, groupMembers }: Props) => {
    const [threadMessages, setThreadMessages] = useState<ChatMessage[]>()
    const [showThreadMessagesDialog, toggleThreadMessagesDialog] = useToggleState(false)
    const { t: tCommon } = _useTranslation('common')
    const authenticatedKnower = useSelector((state: AppState) => state.knower)

    const fetchThreadMessages = useCallback(() => {
        UserChatService.getThreadMessages(chatMessage.chatUuid, chatMessage.uuid)
            .then((messages) => {
                setThreadMessages(messages.reverse())
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'userChat.getChatMembers')
                } else {
                    throw error
                }
            })
    }, [])

    const onThreadClicked = () => {
        fetchThreadMessages()
        toggleThreadMessagesDialog()
    }

    const handleSendMessage = (message: string, filename: string | null, knowUuid: string | null) => {
        if (!chat.uuid) {
            return
        }

        const data = {
            message,
            knowUuid: knowUuid ?? null,
            attachmentPath: filename,
            parentUuid: chatMessage.uuid,
        }

        UserChatService.postMessage(chat.uuid, data)
            .then((newMessage) => {
                trackEvent(AnalyticsEventChatSend, {
                    value: chat.uuid,
                    chat: chat,
                    chatMessage: newMessage,
                    chatPartnerUuid: chat?.chatPartner ? chat?.chatPartner.uuid : null,
                })
                fetchData && fetchData()
                setTimeout(() => {
                    fetchThreadMessages()
                }, 1000)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'userChat.postMessage')
                } else {
                    throw error
                }
            })
    }

    if (!chatMessage.threadMessageCount) return null

    return (
        <>
            <ThreadWrapper onClick={onThreadClicked}>
                <ThreadIcon />
                <Body2 fontFamily="var(--font-inter)" fontWeight={500} color={theme.colors.deepSeaBlue}>
                    {chatMessage.threadMessageCount}
                </Body2>
            </ThreadWrapper>
            {threadMessages?.length ? (
                <FeatureDialog
                    open={showThreadMessagesDialog}
                    onClose={toggleThreadMessagesDialog}
                    ariaPrefix="thread-messages"
                    title="Thread messages"
                    allowVerticalOverflow
                >
                    <ChatDisplay
                        chatMessages={generateDisplayMessages(threadMessages)}
                        ownIdentification={authenticatedKnower?.user.uuid}
                        groupMembers={groupMembers}
                    />
                    <ChatMessageForm onSendMessage={handleSendMessage} contentPrefix={ContentPrefixes.ChatAttachment} />
                </FeatureDialog>
            ) : null}
        </>
    )
}

export default ThreadMessages
